import {Trans} from "@lingui/react/macro";
import {useMemo} from "react";
import {useNavigate, type RouteObject} from "react-router";
import GroupSyncBadge from "../../features/group/GroupSyncBadge";
import AddItem from "../../features/items/AddItem";
import EditItem from "../../features/items/EditItem";
import {ItemPillGrid, ItemPillLayout, MiniPill, QuantityPill} from "../../features/items/ItemPill";
import HumanTime from "../../HumanTime";
import {groupItemsByCategory, useSortedCategories} from "../../models/category-queries";
import type {GroupRepo, ItemEntry, ItemId, ItemVariantEntry} from "../../models/GroupRepo";
import {useIncompleteVariants, useJustCompletedVariants} from "../../models/item-queries";
import type {UserRepo} from "../../models/UserRepo";
import AppShell from "../../shared/ui/AppShell";
import GroupFooterNav from "../../shared/ui/GroupFooterNav";
import Header from "../../shared/ui/Header";
import {blueTheme, tealTheme} from "../../styles/themes/color-themes.css";
import {Col, Row, Text} from "../../ui/Box";
import Button, {FloatingButton} from "../../ui/Button";
import {DrawerRoutes, RouteDrawer} from "../../ui/Drawer";
import Icon from "../../ui/Icon";
import groupByDate from "../../utils";

type UndoneItemTileProps = {
  groupRepo: GroupRepo;
  item: ItemEntry;
  variant: Omit<ItemVariantEntry, "completedAt">;
  navigate: (to: string) => void;
};
const UndoneItemPill = (props: UndoneItemTileProps) => {
  const {item, groupRepo, variant, navigate} = props;
  const handleClick = () => {
    groupRepo.ItemVariants.patch(variant.id, {completedAt: new Date()});
  };
  return (
    <ItemPillLayout
      emoji={item.emoji}
      theme={tealTheme}
      name={item.name}
      onClick={handleClick}
      confirmAnimation="done"
      onLongClick={() => {
        navigate(`edit/${variant.id}`);
      }}
    >
      <Row wrap sp="4" absolute justify="end" bottom="0" left="0" right="0" px="4" pb="4">
        <QuantityPill variant={variant} />
        {variant.variant && <MiniPill>{variant.variant}</MiniPill>}
      </Row>
    </ItemPillLayout>
  );
};

type ItemListByCategoryProps = {
  variants: Omit<ItemVariantEntry, "completedAt">[];
  groupRepo: GroupRepo;
  items: Record<ItemId, ItemEntry>;
};
const ItemListByCategory = (props: ItemListByCategoryProps) => {
  const {variants, groupRepo, items} = props;
  const group = groupRepo.vals.useValues();
  const navigate = useNavigate();
  const catsWithEntries = useSortedCategories(groupRepo, group.id);
  const groups = useMemo(() => {
    const tuples = variants.map((v) => ({variant: v, item: items[v.itemId]}));
    return groupItemsByCategory(
      tuples,
      (t) => t.item.categoryId,
      catsWithEntries.map(({cat}) => cat)
    );
  }, [variants, items, catsWithEntries]);
  return (
    <Col sp="16">
      {groups.map(({category: cat, elements}) => (
        <Col sp="8" key={cat?.id ?? "null"}>
          {cat ? (
            <Text
              size="16"
              color="secondary"
              textOverflow="ellipsis"
              overflow="hidden"
              textWrap="nowrap"
            >
              {cat.emoji} {cat.name}
            </Text>
          ) : (
            <Text>
              <Trans id={"cat.other"}>other</Trans>
            </Text>
          )}
          <ItemPillGrid>
            {elements.map(({item, variant}) => (
              <UndoneItemPill
                key={`${item.id}-${variant.id}`}
                item={item}
                variant={variant}
                groupRepo={groupRepo}
                navigate={navigate}
              />
            ))}
          </ItemPillGrid>
        </Col>
      ))}
    </Col>
  );
};
type DoneItemTileProps = {
  item: ItemEntry;
  groupRepo: GroupRepo;
  variant: ItemVariantEntry;
  navigate: (to: string) => void;
};
const DoneItemPill = (props: DoneItemTileProps) => {
  const {item, navigate, groupRepo, variant} = props;
  const handleClick = () => {
    groupRepo.ItemVariants.patch(variant.id, {completedAt: null});
  };

  return (
    <ItemPillLayout
      emoji={item.emoji}
      theme={blueTheme}
      name={item.name}
      onClick={handleClick}
      confirmAnimation="added"
      onLongClick={() => {
        navigate(`edit/${variant.id}`);
      }}
    >
      <Row wrap sp="4" absolute justify="end" bottom="0" left="0" right="0" px="4" pb="4">
        <QuantityPill variant={variant} />
        {variant.variant && <MiniPill>{variant.variant}</MiniPill>}
      </Row>
    </ItemPillLayout>
  );
};

type ItemsListProps = {
  elements: {item: ItemEntry; variant: ItemVariantEntry}[];
  groupRepo: GroupRepo;
};
const ItemList = (props: ItemsListProps) => {
  const {elements, groupRepo} = props;
  const navigate = useNavigate();
  return (
    <ItemPillGrid>
      {elements.map(({item, variant}) => (
        <DoneItemPill
          key={`${item.id}-${variant.id}`}
          groupRepo={groupRepo}
          variant={variant}
          item={item}
          navigate={navigate}
        />
      ))}
    </ItemPillGrid>
  );
};

const ShoppingList = ({
  groupRepo,
  items,
}: {
  groupRepo: GroupRepo;
  items: Record<ItemId, ItemEntry>;
}) => {
  const incompleteVariants = useIncompleteVariants(groupRepo);
  return incompleteVariants.length === 0 ? (
    <div>No Pending Items</div>
  ) : (
    <ItemListByCategory variants={incompleteVariants} items={items} groupRepo={groupRepo} />
  );
};

const JustPurchasedList = ({
  groupRepo,
  items: allItems,
}: {
  groupRepo: GroupRepo;
  items: Record<ItemId, ItemEntry>;
}) => {
  const variantIds = useJustCompletedVariants(groupRepo, 15);
  const variants = groupRepo.ItemVariants.useAllByIds(variantIds);
  const elements = variants.map((variant) => {
    const item = allItems[variant.itemId];
    return {variant, item};
  });
  const groups = groupByDate({
    sortedItems: elements,
    extractDate: ({variant}) => variant.completedAt!,
  });

  return (
    <>
      <Text as="h2" size="20" color="primary">
        <Trans>Recently purchased</Trans>
      </Text>
      {groups.length === 0 ? (
        <Text color="secondary" size="14_tight">
          <Trans>Nothing</Trans>
        </Text>
      ) : (
        groups.map(({items: groupItems, newestDate}) => (
          <Col key={newestDate.getTime()} sp="8">
            <Text size="16" color="secondary">
              <HumanTime date={newestDate} />
            </Text>
            <ItemList elements={groupItems} groupRepo={groupRepo} />
          </Col>
        ))
      )}
    </>
  );
};

const getModalRoutes = (groupRepo: GroupRepo): RouteObject[] => [
  {
    path: "add",
    element: (
      <RouteDrawer backPath=".." title="Add Item">
        <AddItem groupRepo={groupRepo} />
      </RouteDrawer>
    ),
  },
  {
    path: "edit/:variantId",
    element: (
      <RouteDrawer backPath=".." title="Edit Item">
        <EditItem groupRepo={groupRepo} />
      </RouteDrawer>
    ),
  },
];

const ItemOverview = ({groupRepo, userRepo}: {groupRepo: GroupRepo; userRepo: UserRepo}) => {
  const group = groupRepo.vals.useValues();
  const {isSyncing} = userRepo.vals.useValues();
  const items = groupRepo.Items.useGetAll();
  return (
    <AppShell
      header={
        <Header
          title={group.name}
          actions={
            <>
              {isSyncing && <GroupSyncBadge connectionKey={group.id} />}
              <Button variant="tertiary" to="categories">
                <Icon name="sort" />
              </Button>
              <Button variant="tertiary" to="settings">
                <Icon name="vert-ellipsis" />
              </Button>
            </>
          }
        />
      }
      footer={<GroupFooterNav current="list" group={group} />}
    >
      <Col px="12" py="8" sp="24">
        <ShoppingList groupRepo={groupRepo} items={items} />
        <JustPurchasedList groupRepo={groupRepo} items={items} />
        <FloatingButton to="add">
          <Icon name="plus" />
        </FloatingButton>
      </Col>
      <DrawerRoutes routes={getModalRoutes(groupRepo)} key={group.id} />
    </AppShell>
  );
};

export default ItemOverview;
